import { matchHistoricalOrderLine, hasLiveCatalogueIdentity, historicalProductName, historicalSku, historicalVariantOptions, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import { useAuth } from "@/context/AuthContext";
import { canOperateInventory, canManageSalesOrders } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { money, fmtDateTime, variantLabel } from "@/lib/format";
import { getActiveCurrencyCode } from "@/lib/currency";
import * as returnsApi from "@/lib/api/returns";
import {
  CURRENT_INVENTORY_VALUE_COPY,
  HISTORICAL_SALE_VALUE_COPY,
  defaultFinalizeDispositionFromCondition,
  DO_NOT_RESTOCK_COPY,
  mapFinalizeDispositionToRpc,
  marketplaceRefundMustUseProviderReadback,
  RETURN_PHYSICAL_NOTICE,
  RETURN_REFUND_OPTIONAL_COPY,
  restockFinalizeCopy,
  composeCompensation,
  refundedUnfulfilledCancellableQty,
  requiresUnfulfilledDisposition,
  seedReturnBoundQty,
  type CompensationMethod,
  type RefundLineState,
  type UnfulfilledRefundDisposition,
} from "@/lib/reverse-lifecycle";
import { RefundComposer } from "@/components/feature/RefundComposer";
import { BarcodeScanInput } from "@/components/feature/BarcodeScanInput";
import { resolveBarcode } from "@/lib/barcode";

interface ReturnRow {
  id: string;
  return_number: string;
  sales_order_id: string | null;
  customer_id: string | null;
  location_id: string | null;
  status: string;
  reason: string | null;
  received_at: string | null;
  notes: string | null;
  created_at: string;
  customers?: { name: string } | null;
  locations?: { name: string } | null;
  sales_orders?: { order_number: string } | null;
}

interface ReturnItemRow extends CatalogueHistoryFields {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  sales_order_item_id: string | null;
  qty_returned: number;
  condition: string;
  disposition: string | null;
  restocked: boolean;
  notes: string | null;
  product_name: string | null;
  sku: string | null;
  variant_options: Record<string, string> | null;
  products?: { name: string; sku: string; selling_price: number } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface RefundRow {
  id: string;
  amount: number;
  currency: string;
  status: string;
  reason: string | null;
  provider: string | null;
  provider_refund_id: string | null;
  provider_sync_status: string | null;
  created_at: string;
}

interface ExchangeRow {
  id: string;
  order_number: string;
  status: string;
  created_at: string;
}

interface SoItemForRefund extends CatalogueHistoryFields {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_ordered: number;
  qty_fulfilled: number;
  qty_cancelled: number | null;
  total: number;
  product_name: string | null;
  sku: string | null;
  variant_attributes: Record<string, string> | null;
  is_bundle: boolean | null;
  products?: { name?: string; sku?: string; selling_price?: number } | null;
  variants?: { sku?: string | null; option_values?: Record<string, string> | null } | null;
}

interface LinkedOrder {
  id: string;
  order_number: string;
  total: number;
  shipping: number | null;
  tax: number | null;
  currency: string | null;
  source: string | null;
  channel_id: string | null;
  connectors?: { name?: string; provider?: string | null } | null;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const conditionLabel: Record<string, string> = {
  resellable: "Resellable",
  defective: "Defective",
  damaged: "Damaged",
};

const dispositionLabel: Record<string, string> = {
  restock: "Restock",
  scrap: "Scrap",
  reject: "Reject / refund",
  quarantine: "Quarantine",
};

function soLineForReturnItem(
  item: ReturnItemRow,
  soItems: SoItemForRefund[],
) {
  return matchHistoricalOrderLine(item, soItems);
}

function historicalMerchandiseForReturnLine(
  item: ReturnItemRow,
  soItems: SoItemForRefund[],
): number | null {
  const line = soLineForReturnItem(item, soItems);
  if (!line || !(line.qty_ordered > 0)) return null;
  return (Number(line.total ?? 0) / line.qty_ordered) * item.qty_returned;
}

function refundLineStatesFromOrder(
  soItems: SoItemForRefund[],
  refunds: Array<{ id: string; status: string; shipping_amount?: number | null }>,
  refundItems: Array<{ sales_refund_id: string; sales_order_item_id: string; qty: number; amount: number }>,
): RefundLineState[] {
  const acceptedIds = new Set(refunds.filter((r) => r.status !== "rejected" && r.status !== "failed").map((r) => r.id));
  return soItems.filter((it) => !it.is_bundle).map((it) => {
    const rows = refundItems.filter((ri) => ri.sales_order_item_id === it.id && acceptedIds.has(ri.sales_refund_id));
    return {
      salesOrderItemId: it.id,
      productName: it.product_name ?? it.products?.name ?? null,
      sku: it.sku ?? it.variants?.sku ?? it.products?.sku ?? null,
      variantLabel: variantLabel(it.variant_attributes ?? it.variants?.option_values) || null,
      qtyOrdered: it.qty_ordered,
      qtyAlreadyRefunded: rows.reduce((s, r) => s + Number(r.qty ?? 0), 0),
      lineCharged: it.total,
      amountAlreadyRefunded: rows.reduce((s, r) => s + Number(r.amount ?? 0), 0),
    };
  });
}

export default function ReturnDetail() {
  const { id } = useParams();
  const { org, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [refundQty, setRefundQty] = useState<Record<string, number>>({});
  const [refundIncludeShipping, setRefundIncludeShipping] = useState(false);
  const [refundAdjustment, setRefundAdjustment] = useState("");
  const [refundAdjustmentReason, setRefundAdjustmentReason] = useState("");
  const [refundMethod, setRefundMethod] = useState<CompensationMethod>("original_payment");
  const [refundReason, setRefundReason] = useState("");
  const [refundDisposition, setRefundDisposition] = useState<UnfulfilledRefundDisposition | "">("");
  const [syncProviderRefund, setSyncProviderRefund] = useState(false);
  const [dispositions, setDispositions] = useState<Record<string, { disposition: string; qty: number }>>({});
  const [saving, setSaving] = useState(false);
  const [receiveSaving, setReceiveSaving] = useState(false);
  const [refundSaving, setRefundSaving] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [refundIdemKey, setRefundIdemKey] = useState("");
  const [exchangeIdemKey, setExchangeIdemKey] = useState("");
  const [exchangeSaving, setExchangeSaving] = useState(false);
  const [error, setError] = useState("");
  const [identifyFeedback, setIdentifyFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [identified, setIdentified] = useState<{ name: string; condition: string; disposition: string | null; qty: number } | null>(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [ackRefundId, setAckRefundId] = useState("");
  const [ackProvider, setAckProvider] = useState("");
  const [ackProviderRef, setAckProviderRef] = useState("");
  const [ackSaving, setAckSaving] = useState(false);
  const [ackError, setAckError] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["return-detail", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [ret, items, refunds, exchanges] = await Promise.all([
        supabase
          .from("sales_returns")
          .select("id, return_number, sales_order_id, customer_id, location_id, status, reason, received_at, notes, created_at, customers(name), locations(name), sales_orders!sales_order_id(order_number)")
          .eq("org_id", org!.id)
          .eq("id", id)
          .maybeSingle(),
        supabase
          .from("sales_return_items")
          .select("catalogue_product_identity_id, catalogue_variant_identity_id, id, product_id, variant_id, sales_order_item_id, qty_returned, condition, disposition, restocked, notes, product_name, sku, variant_options, products(name, sku, selling_price), variants(sku, option_values)")
          .eq("org_id", org!.id)
          .eq("sales_return_id", id)
          .order("created_at"),
        supabase
          .from("sales_refunds")
          .select("id, amount, currency, status, reason, provider, provider_refund_id, provider_sync_status, created_at")
          .eq("org_id", org!.id)
          .eq("return_id", id)
          .order("created_at", { ascending: false }),
        supabase
          .from("sales_orders")
          .select("id, order_number, status, created_at")
          .eq("org_id", org!.id)
          .eq("source_return_id", id)
          .eq("is_exchange", true)
          .order("created_at", { ascending: false }),
      ]);
      const soId = ret.data?.sales_order_id ?? null;
      const [orderRes, soItemsRes, orderRefundsRes] = soId
        ? await Promise.all([
            supabase
              .from("sales_orders")
              .select("id, order_number, total, shipping, tax, currency, source, channel_id, connectors(name, provider)")
              .eq("org_id", org!.id)
              .eq("id", soId)
              .maybeSingle(),
            supabase
              .from("sales_order_items")
              .select("catalogue_product_identity_id, catalogue_variant_identity_id, id, product_id, variant_id, qty_ordered, qty_fulfilled, qty_cancelled, total, product_name, sku, variant_attributes, is_bundle, products(name, sku, selling_price), variants(sku, option_values)")
              .eq("org_id", org!.id)
              .eq("sales_order_id", soId),
            supabase
              .from("sales_refunds")
              .select("id, amount, currency, status, reason, shipping_amount, created_at")
              .eq("org_id", org!.id)
              .eq("sales_order_id", soId)
              .order("created_at", { ascending: false }),
          ])
        : [{ data: null }, { data: [] }, { data: [] }];
      const orderRefundIds = (orderRefundsRes.data ?? []).map((r: { id: string }) => r.id);
      const refundItemsRes = orderRefundIds.length === 0
        ? { data: [] }
        : await supabase
            .from("sales_refund_items")
            .select("sales_refund_id, sales_order_item_id, sku, qty, amount")
            .eq("org_id", org!.id)
            .in("sales_refund_id", orderRefundIds);
      return {
        ret: ret.data
          ? {
              ...ret.data,
              customers: firstRelation(ret.data.customers),
              locations: firstRelation(ret.data.locations),
              sales_orders: firstRelation(ret.data.sales_orders),
            } as ReturnRow
          : null,
        items: (items.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as ReturnItemRow[],
        refunds: (refunds.data ?? []) as RefundRow[],
        exchanges: (exchanges.data ?? []) as ExchangeRow[],
        order: orderRes.data
          ? {
              ...(orderRes.data as LinkedOrder),
              connectors: firstRelation((orderRes.data as { connectors?: unknown }).connectors) as LinkedOrder["connectors"],
            }
          : null,
        soItems: (soItemsRes.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as SoItemForRefund[],
        orderRefunds: (orderRefundsRes.data ?? []) as Array<{ id: string; amount: number; status: string; shipping_amount?: number | null }>,
        refundItems: (refundItemsRes.data ?? []) as Array<{ sales_refund_id: string; sales_order_item_id: string; qty: number; amount: number }>,
      };
    },
  });

  const openFinalize = () => {
    const init: Record<string, { disposition: string; qty: number }> = {};
    (data?.items ?? []).forEach((it) => {
      if (it.restocked) return;
      init[it.id] = {
        disposition: defaultFinalizeDispositionFromCondition(it.condition),
        qty: it.qty_returned,
      };
    });
    setDispositions(init);
    setError("");
    setFinalizeOpen(true);
  };

  const handleFinalize = async () => {
    setError("");
    const pending = (data?.items ?? []).filter((it) => !it.restocked);
    const missing = pending.some((it) => !mapFinalizeDispositionToRpc(dispositions[it.id]?.disposition ?? ""));
    if (missing) {
      setError("Choose RESTOCK or DO NOT RESTOCK for each item. Resellable does not restock by itself.");
      return;
    }
    setSaving(true);
    const pLines = pending.map((it) => {
      const mapped = mapFinalizeDispositionToRpc(dispositions[it.id]?.disposition ?? "")!;
      return {
        item_id: it.id,
        disposition: mapped,
        restock_qty: mapped === "restock" ? dispositions[it.id]?.qty ?? 0 : 0,
      };
    });
    if (pLines.length === 0) {
      setSaving(false);
      setError("No items left to process.");
      return;
    }
    try {
      await returnsApi.finalizeSalesReturn(id!, pLines, data?.ret?.location_id ?? null);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setFinalizeOpen(false);
    queryClient.invalidateQueries({ queryKey: ["return-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["returns", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleReceive = async () => {
    setError("");
    setReceiveSaving(true);
    try {
      await returnsApi.receiveSalesReturn(id!);
    } catch (e) {
      setReceiveSaving(false);
      setError((e as Error).message);
      setReceiveOpen(false);
      return;
    }
    setReceiveSaving(false);
    setReceiveOpen(false);
    queryClient.invalidateQueries({ queryKey: ["return-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["returns", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleIdentify = async (value: string) => {
    setIdentifyFeedback(null);
    setIdentified(null);
    try {
      const res = await resolveBarcode(org!.id, value);
      if (!res.found) {
        setIdentifyFeedback({ ok: false, text: `No product found for "${value}".` });
        return;
      }
      if (res.ambiguous) {
        setIdentifyFeedback({ ok: false, text: "Ambiguous barcode — multiple matches." });
        return;
      }
      const m = res.matches[0];
      const item = (data?.items ?? []).find(
        (it) => it.product_id === m.product_id && (it.variant_id ?? null) === (m.variant_id ?? null),
      );
      if (!item) {
        setIdentifyFeedback({ ok: false, text: `${m.name} is not part of this return.` });
        return;
      }
      setIdentified({
        name: historicalProductName(item),
        condition: item.condition,
        disposition: item.disposition,
        qty: item.qty_returned,
      });
      setIdentifyFeedback({ ok: true, text: `Identified: ${historicalProductName(item)}` });
    } catch (err) {
      setIdentifyFeedback({ ok: false, text: (err as Error).message });
    }
  };

  const openRefund = () => {
    setRefundError("");
    setRefundIdemKey(crypto.randomUUID());
    setRefundIncludeShipping(false);
    setRefundAdjustment("");
    setRefundAdjustmentReason("");
    setRefundMethod("original_payment");
    setRefundReason("");
    setRefundDisposition("");
    setSyncProviderRefund(false);
    const lines = refundLineStatesFromOrder(data?.soItems ?? [], data?.orderRefunds ?? [], data?.refundItems ?? []);
    const returnedQtyByLine: Record<string, number> = {};
    for (const it of data?.items ?? []) {
      const soLine = soLineForReturnItem(it, data?.soItems ?? []);
      if (!soLine) continue;
      returnedQtyByLine[soLine.id] = (returnedQtyByLine[soLine.id] ?? 0) + it.qty_returned;
    }
    setRefundQty(seedReturnBoundQty(lines, returnedQtyByLine));
    setRefundOpen(true);
  };

  const handleCreateRefund = async () => {
    setRefundError("");
    const current = data?.ret;
    if (!current?.sales_order_id || !data?.order) {
      setRefundError("This return is not linked to a sales order.");
      return;
    }
    const lines = refundLineStatesFromOrder(data.soItems ?? [], data.orderRefunds ?? [], data.refundItems ?? []);
    const already = (data.orderRefunds ?? []).filter((rf: { status: string }) => rf.status !== "rejected" && rf.status !== "failed").reduce((s: number, rf: { amount: number }) => s + Number(rf.amount ?? 0), 0);
    const quote = composeCompensation({
      chargedTotal: data.order.total,
      alreadyRefunded: already,
      tax: data.order.tax,
      orderShipping: data.order.shipping,
      shippingAlreadyRefunded: (data.orderRefunds ?? [])
        .filter((rf: { status: string }) => rf.status !== "rejected" && rf.status !== "failed")
        .reduce((s: number, rf: { shipping_amount?: number | null }) => s + Number(rf.shipping_amount ?? 0), 0),
      lines,
      selections: Object.entries(refundQty).map(([salesOrderItemId, qty]) => ({ salesOrderItemId, qty })),
      includeShipping: refundIncludeShipping,
      adjustmentAmount: refundAdjustment,
      adjustmentReason: refundAdjustmentReason,
      method: refundMethod,
      provider: data.order.connectors?.provider ?? data.order.source,
    });
    if (quote.ok === false) {
      setRefundError(quote.message);
      return;
    }
    setRefundSaving(true);
    try {
      await returnsApi.createSalesRefund({
        salesOrderId: current.sales_order_id,
        returnId: current.id,
        amount: quote.quote.mode === "order_level" ? quote.quote.amount : null,
        reason: refundReason || refundAdjustmentReason || null,
        idempotencyKey: refundIdemKey,
        mode: quote.quote.mode,
        lines: quote.quote.lines.map((l) => ({ salesOrderItemId: l.salesOrderItemId, qty: l.qty })),
        shippingAmount: quote.quote.shipping,
        adjustmentAmount: quote.quote.adjustment,
      });
    } catch (e) {
      setRefundSaving(false);
      setRefundError((e as Error).message);
      return;
    }
    setRefundSaving(false);
    setRefundOpen(false);
    setRefundQty({});
    setRefundReason("");
    queryClient.invalidateQueries({ queryKey: ["return-detail", org?.id, id] });
  };

  const handleCreateExchange = async () => {
    if (!r.sales_order_id) return;
    setExchangeSaving(true);
    // Route through the trusted transactional RPC: the exchange Sales Order is
    // created atomically with a per-org sequence number (RX-YYYY-000001), is
    // linked to the source return and original order, carries is_exchange +
    // source='exchange', and now includes the returned items as replacement
    // lines so the order is created 'confirmed' and immediately processable.
    if ((data?.items ?? []).some((it) => !hasLiveCatalogueIdentity(it))) {
      setExchangeSaving(false);
      setExchangeOpen(false);
      setError("A returned catalogue identity has been deleted. Choose a new active replacement explicitly; never match the original by SKU.");
      return;
    }
    const pLines = (data?.items ?? []).filter(hasLiveCatalogueIdentity).map((it) => ({
      product_id: it.product_id,
      variant_id: it.variant_id,
      qty: it.qty_returned,
      unit_price: Number(it.products?.selling_price ?? 0),
    }));
    let newId: string;
    try {
      newId = await returnsApi.createExchangeOrder({
        returnId: r.id,
        lines: pLines,
        idempotencyKey: exchangeIdemKey || undefined,
      });
    } catch (e) {
      setExchangeSaving(false);
      setError((e as Error).message);
      return;
    }
    setExchangeSaving(false);
    setExchangeOpen(false);
    navigate(`/sales-orders/${newId}`);
  };

  const openAck = (refundId: string) => {
    setAckError("");
    setAckRefundId(refundId);
    setAckProvider("");
    setAckProviderRef("");
    setAckOpen(true);
  };

  const handleAck = async () => {
    setAckError("");
    setAckSaving(true);
    try {
      await returnsApi.recordRefundAcknowledgement({
        refundId: ackRefundId,
        provider: ackProvider || null,
        providerRefundId: ackProviderRef || null,
      });
    } catch (e) {
      setAckSaving(false);
      setAckError((e as Error).message);
      return;
    }
    setAckSaving(false);
    setAckOpen(false);
    queryClient.invalidateQueries({ queryKey: ["return-detail", org?.id, id] });
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.ret) {
    return (
      <div>
        <PageHeader title="Return" backTo="/returns" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this return.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const r = data.ret;
  const canOperate = canOperateInventory(role);
  const canRefund = canManageSalesOrders(role);
  const pendingItems = data.items.filter((it) => !it.restocked && r.status !== "completed");
  const restockValue = data.items
    .filter((it) => it.restocked)
    .reduce((s, it) => s + Number(it.products?.selling_price ?? 0) * it.qty_returned, 0);
  const historicalReturnedValue = data.items.reduce((s, it) => {
    const paid = historicalMerchandiseForReturnLine(it, data.soItems ?? []);
    return paid == null ? s : s + paid;
  }, 0);
  const refundLines = refundLineStatesFromOrder(data.soItems ?? [], data.orderRefunds ?? [], data.refundItems ?? []);
  const returnedQtyByLine: Record<string, number> = {};
  for (const it of data.items) {
    const soLine = soLineForReturnItem(it, data.soItems ?? []);
    if (!soLine) continue;
    returnedQtyByLine[soLine.id] = (returnedQtyByLine[soLine.id] ?? 0) + it.qty_returned;
  }
  const alreadyRefunded = (data.orderRefunds ?? [])
    .filter((rf: { status: string }) => rf.status !== "rejected" && rf.status !== "failed")
    .reduce((s: number, rf: { amount: number }) => s + Number(rf.amount ?? 0), 0);
  const shippingAlready = (data.orderRefunds ?? [])
    .filter((rf: { status: string }) => rf.status !== "rejected" && rf.status !== "failed")
    .reduce((s: number, rf: { shipping_amount?: number | null }) => s + Number(rf.shipping_amount ?? 0), 0);
  const currentRefundQuote = data.order
    ? composeCompensation({
        chargedTotal: data.order.total,
        alreadyRefunded,
        tax: data.order.tax,
        orderShipping: data.order.shipping,
        shippingAlreadyRefunded: shippingAlready,
        lines: refundLines,
        selections: Object.entries(refundQty).map(([salesOrderItemId, qty]) => ({ salesOrderItemId, qty })),
        includeShipping: refundIncludeShipping,
        adjustmentAmount: refundAdjustment,
        adjustmentReason: refundAdjustmentReason,
        method: refundMethod,
        provider: data.order.connectors?.provider ?? data.order.source,
      })
    : { ok: false as const, code: "NO_ORDER", message: "No linked sales order." };
  const previewQuote = currentRefundQuote.ok ? currentRefundQuote.quote : null;
  const unfulfilledRefundPreviewQty = previewQuote
    ? previewQuote.lines.reduce((sum, line) => {
        const item = (data.soItems ?? []).find((it) => it.id === line.salesOrderItemId);
        if (!item) return sum;
        return sum + refundedUnfulfilledCancellableQty({
          qtyOrdered: item.qty_ordered,
          qtyFulfilled: item.qty_fulfilled ?? 0,
          qtyCancelled: item.qty_cancelled ?? 0,
          qtyRefunded: line.qty,
          qtyAlreadyCancelledFromRefund: 0,
        });
      }, 0)
    : 0;
  const compensatedByRefund = data.refunds.some((rf) => rf.status !== "rejected" && rf.status !== "failed");
  const compensatedByExchange = data.exchanges.length > 0;

  return (
    <div>
      <PageHeader title={r.return_number} subtitle={r.customers?.name ?? "—"} backTo="/returns">
        <div className="flex items-center gap-2">
          {canOperate && r.status === "draft" && (
            <button
              onClick={() => setReceiveOpen(true)}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-inbox-archive-line" /> Mark Received
            </button>
          )}
          {canOperate && r.status === "received" && pendingItems.length > 0 && (
            <button
              onClick={openFinalize}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-check-double-line" /> Inspect &amp; Finalize
            </button>
          )}
          {canRefund && r.status === "completed" && r.sales_order_id && !compensatedByExchange && (
            <button
              onClick={openRefund}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-amber-50 hover:text-amber-700 text-slate-600 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-refund-2-line" /> Issue refund (optional)
            </button>
          )}
          {canRefund && r.status === "completed" && r.sales_order_id && !compensatedByRefund && (
            <button
              onClick={() => { setExchangeIdemKey(crypto.randomUUID()); setExchangeOpen(true); }}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-emerald-50 hover:text-emerald-700 text-slate-600 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-refresh-line" /> Create Exchange
            </button>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <StatusBadge status={r.status} />
              <span className="text-sm text-slate-500">Created {fmtDateTime(r.created_at)}</span>
              {r.received_at && <span className="text-sm text-slate-500">Received {fmtDateTime(r.received_at)}</span>}
            </div>
            <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-3 mb-4">{RETURN_PHYSICAL_NOTICE}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400">Customer</p>
                <p className="text-sm font-medium text-slate-800">{r.customers?.name ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Receiving location</p>
                <p className="text-sm font-medium text-slate-800">{r.locations?.name ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Original order</p>
                {r.sales_order_id ? (
                  <button
                    onClick={() => navigate(`/sales-orders/${r.sales_order_id}`)}
                    className="text-sm font-medium text-emerald-700 hover:text-emerald-800 font-mono whitespace-nowrap"
                  >
                    {r.sales_orders?.order_number ?? r.sales_order_id}
                  </button>
                ) : (
                  <p className="text-sm font-medium text-slate-400">—</p>
                )}
              </div>
            </div>
            {r.reason && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400 mb-1">Reason</p>
                <p className="text-sm text-slate-700">{r.reason}</p>
              </div>
            )}
            {r.notes && (
              <div className="mt-4">
                <p className="text-xs text-slate-400 mb-1">Notes</p>
                <p className="text-sm text-slate-600">{r.notes}</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800">Returned Items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Qty</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Condition</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Disposition</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Historical paid</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Inventory value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={it.id} className="border-b border-slate-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-800">
                          {historicalProductName(it)}
                          {(variantLabel(historicalVariantOptions(it)) || it.sku || it.variants?.sku) && (
                            <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(it)) || it.sku || it.variants?.sku}</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400 font-mono">{historicalSku(it)}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{it.qty_returned}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{conditionLabel[it.condition] ?? it.condition}</td>
                      <td className="px-4 py-3">
                        {it.disposition ? (
                          <StatusBadge status={it.disposition === "restock" ? "received" : it.disposition === "scrap" ? "warning" : "inactive"} customLabel={dispositionLabel[it.disposition] ?? it.disposition} />
                        ) : (
                          <span className="text-xs text-slate-400">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{money(historicalMerchandiseForReturnLine(it, data.soItems ?? []) ?? 0)}</td>
                      <td className="px-4 py-3 text-sm text-slate-400 text-right">{money(Number(it.products?.selling_price ?? 0) * it.qty_returned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Identify item</h3>
            <p className="text-xs text-slate-500 mb-3">Scan a returned item to confirm it belongs to this return.</p>
            <BarcodeScanInput onScan={handleIdentify} feedback={identifyFeedback} placeholder="Scan barcode or SKU" />
            {identified && (
              <div className="mt-3 bg-slate-50 border border-slate-100 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-800">{identified.name}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Qty {identified.qty} · {conditionLabel[identified.condition] ?? identified.condition}
                  {identified.disposition ? ` · ${dispositionLabel[identified.disposition] ?? identified.disposition}` : " · disposition pending"}
                </p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Summary</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Items returned</span>
                <span className="font-medium text-slate-800">{data.items.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Restocked</span>
                <span className="font-medium text-emerald-700">{data.items.filter((it) => it.restocked).length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Historical merchandise paid</span>
                <span className="font-medium text-slate-800">{money(historicalReturnedValue)}</span>
              </div>
              <p className="text-xs text-slate-400">{HISTORICAL_SALE_VALUE_COPY}</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Current inventory / Product Master value</span>
                <span className="font-medium text-slate-800">{money(restockValue)}</span>
              </div>
              <p className="text-xs text-slate-400">{CURRENT_INVENTORY_VALUE_COPY}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Compensation</h3>
            {compensatedByExchange ? (
              <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-emerald-800">Exchanged</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Replacement goods issued</p>
                </div>
                {data.exchanges[0] && (
                  <button
                    onClick={() => navigate(`/sales-orders/${data.exchanges[0].id}`)}
                    className="text-xs font-medium text-emerald-700 hover:text-emerald-800 font-mono whitespace-nowrap"
                  >
                    {data.exchanges[0].order_number}
                  </button>
                )}
              </div>
            ) : compensatedByRefund ? (
              <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-100 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium text-amber-800">Refunded</p>
                  <p className="text-xs text-amber-600 mt-0.5">Financial compensation recorded</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">{RETURN_REFUND_OPTIONAL_COPY}</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Refunds</h3>
            {data.refunds.length === 0 ? (
              <p className="text-xs text-slate-400">No refunds recorded for this return.</p>
            ) : (
              <div className="space-y-3">
                {data.refunds.map((rf) => (
                  <div key={rf.id} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-100 rounded-lg p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{rf.currency} {Number(rf.amount).toFixed(2)}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {rf.reason || "No reason"}
                        {rf.provider_refund_id && <span className="block font-mono">{rf.provider_refund_id}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${rf.status === "refunded" || rf.provider_sync_status === "confirmed" ? "bg-emerald-100 text-emerald-700" : rf.provider_sync_status === "error" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {rf.provider_sync_status === "confirmed" || rf.status === "refunded" ? "Refunded" : rf.provider_sync_status === "error" ? "Channel error" : "Pending"}
                      </span>
                      {canRefund && rf.status === "pending" && !marketplaceRefundMustUseProviderReadback(data.order?.source) && (
                        <button onClick={() => openAck(rf.id)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap">
                          Acknowledge
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">How disposition works</h3>
            <ul className="space-y-2 text-xs text-slate-500 leading-relaxed">
              <li><span className="font-medium text-emerald-700">Restock</span> — return the item to sellable inventory (adds to on-hand).</li>
              <li><span className="font-medium text-amber-700">Scrap</span> — write the item off; no inventory impact.</li>
              <li><span className="font-medium text-orange-700">Quarantine</span> — hold the item for inspection; no inventory impact.</li>
              <li><span className="font-medium text-slate-700">Reject</span> — item not accepted, no inventory impact.</li>
            </ul>
          </div>
        </div>
      </div>

      <Modal
        open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        title={`Inspect & Finalize — ${r.return_number}`}
        wide
        footer={
          <>
            <button onClick={() => setFinalizeOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={handleFinalize} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Finalizing..." : "Confirm Dispositions"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <p className="text-xs text-slate-600">{RETURN_PHYSICAL_NOTICE}</p>
          <p className="text-xs text-slate-500">
            Choose RESTOCK or DO NOT RESTOCK for each item. Resellable condition does not restock by itself.
          </p>
          {(data.items ?? []).filter((it) => !it.restocked).map((it) => {
            const d = dispositions[it.id] ?? { disposition: defaultFinalizeDispositionFromCondition(it.condition), qty: it.qty_returned };
            return (
              <div key={it.id} className="bg-slate-50 border border-slate-100 rounded-lg p-3 space-y-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {historicalProductName(it)}
                    {(variantLabel(historicalVariantOptions(it)) || it.sku || it.variants?.sku) && (
                      <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(it)) || it.sku || it.variants?.sku}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-400">
                    {conditionLabel[it.condition] ?? it.condition} · qty {it.qty_returned}
                  </p>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name={`disp-${it.id}`}
                    checked={d.disposition === "restock"}
                    disabled={!hasLiveCatalogueIdentity(it)}
                    onChange={() => setDispositions((s) => ({ ...s, [it.id]: { disposition: "restock", qty: d.qty } }))}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-semibold">RESTOCK</span>
                    {!hasLiveCatalogueIdentity(it) && <span className="block text-xs text-amber-700">This catalogue identity was deleted. Do not restock it into a replacement SKU.</span>}
                    <span className="block text-xs text-slate-500 mt-0.5">{restockFinalizeCopy(r.locations?.name ?? "")}</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name={`disp-${it.id}`}
                    checked={d.disposition === "do_not_restock"}
                    onChange={() => setDispositions((s) => ({ ...s, [it.id]: { disposition: "do_not_restock", qty: d.qty } }))}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-semibold">DO NOT RESTOCK</span>
                    <span className="block text-xs text-slate-500 mt-0.5">{DO_NOT_RESTOCK_COPY}</span>
                  </span>
                </label>
                {d.disposition === "restock" ? (
                  <input
                    type="number"
                    min="0"
                    max={it.qty_returned}
                    value={d.qty}
                    onChange={(e) =>
                      setDispositions((s) => ({ ...s, [it.id]: { disposition: d.disposition, qty: Number(e.target.value) } }))
                    }
                    className="w-24 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </Modal>

      <ConfirmDialog
        open={receiveOpen}
        title="Mark return received"
        message={`Confirm that ${r.return_number} has been physically received at ${r.locations?.name ?? "the receiving location"}? This records receipt but does not restock — you'll decide each item's disposition next.`}
        confirmLabel="Mark Received"
        loading={receiveSaving}
        onCancel={() => setReceiveOpen(false)}
        onConfirm={handleReceive}
      />

      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title={`Issue Refund — ${r.return_number}`}
        wide
        footer={
          <>
            <button onClick={() => setRefundOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCreateRefund} disabled={refundSaving || !previewQuote || (requiresUnfulfilledDisposition(unfulfilledRefundPreviewQty) && refundDisposition === "")} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {refundSaving ? "Creating..." : "Create compensation record"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {refundError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{refundError}</div>}
          {data.order ? (
            <RefundComposer
              currency={data.order.currency ?? "AUD"}
              chargedTotal={data.order.total}
              alreadyRefunded={alreadyRefunded}
              tax={data.order.tax}
              orderShipping={data.order.shipping}
              shippingAlreadyRefunded={shippingAlready}
              lines={refundLines}
              qtyByLine={refundQty}
              onQtyChange={(salesOrderItemId, qty) => setRefundQty((prev) => ({ ...prev, [salesOrderItemId]: qty }))}
              includeShipping={refundIncludeShipping}
              onIncludeShippingChange={setRefundIncludeShipping}
              adjustmentAmount={refundAdjustment}
              onAdjustmentAmountChange={setRefundAdjustment}
              adjustmentReason={refundAdjustmentReason}
              onAdjustmentReasonChange={setRefundAdjustmentReason}
              reason={refundReason}
              onReasonChange={setRefundReason}
              method={refundMethod}
              onMethodChange={setRefundMethod}
              provider={data.order.connectors?.provider ?? data.order.source}
              returnContext={{
                returnNumber: r.return_number,
                status: r.status === "completed" ? "Received · Finalized" : r.status,
                disposition: data.items.find((it) => it.disposition)?.disposition ?? null,
                returnedQtyByLine,
              }}
              syncProvider={syncProviderRefund}
              onSyncProviderChange={setSyncProviderRefund}
              allowProviderSync={false}
              unfulfilledRefundQty={unfulfilledRefundPreviewQty}
              disposition={refundDisposition}
              onDispositionChange={setRefundDisposition}
              productMasterPrice={data.items[0]?.products?.selling_price}
            />
          ) : (
            <p className="text-sm text-slate-500">This return is not linked to a historical sales order, so refund authority cannot be composed.</p>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={exchangeOpen}
        title="Create Exchange Order"
        message={`Create a replacement Sales Order for the returned items? The order will include the returned items and can be allocated and fulfilled immediately.`}
        confirmLabel="Create Exchange Order"
        loading={exchangeSaving}
        onCancel={() => setExchangeOpen(false)}
        onConfirm={handleCreateExchange}
      />

      <Modal
        open={ackOpen}
        onClose={() => setAckOpen(false)}
        title="Acknowledge Refund"
        footer={
          <>
            <button onClick={() => setAckOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleAck} disabled={ackSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {ackSaving ? "Recording..." : "Mark Refunded"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {ackError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{ackError}</div>}
          <p className="text-xs text-slate-500">
            Record that your payment provider has actually processed this refund. This keeps the operational record separate from the provider acknowledgement.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Provider</label>
            <input type="text" value={ackProvider} onChange={(e) => setAckProvider(e.target.value)} placeholder="e.g. Stripe, PayPal" className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Provider refund ID</label>
            <input type="text" value={ackProviderRef} onChange={(e) => setAckProviderRef(e.target.value)} placeholder="e.g. re_123..." className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
          </div>
        </div>
      </Modal>
    </div>
  );
}